import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { buildCoverageWindow, utcDayToIso } from '../../utils/billCoverage';
import { formatCurrency, formatDate, formatDateRange, formatTileAmount } from '../../utils/billFormat';

const MAX_ISSUES_SHOWN = 5;

const STATUS_CLASS = {
  billed: 'bg-[var(--primary)] text-[var(--bg-card)] border border-transparent',
  covered:
    'bg-[var(--secondary-container)] text-[var(--secondary-container-text)] border border-transparent',
  missing: 'bg-[var(--error-container)] text-[var(--error)] border border-transparent',
  in_progress: 'bg-[var(--bg-hover)] text-[var(--text-secondary)] border border-[var(--primary)]',
  upcoming: 'bg-[var(--bg-hover)] text-[var(--text-muted)] border border-dashed border-[var(--border)]',
  no_history: 'bg-[var(--bg-hover)] text-[var(--text-muted)] border border-[var(--border)] opacity-50',
};

const dayLabel = (day) => formatDate(utcDayToIso(day));
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const describeTile = (m) => {
  const parts = [];
  if (m.status === 'billed') {
    const count = m.bills.length > 1 ? ` (${m.bills.length} bills)` : '';
    parts.push(`${m.longLabel}: ${formatCurrency(m.amount)}${count}.`);
  } else if (m.status === 'covered') {
    const by = m.coveredBy.map((b) => formatDateRange(b.period_start, b.period_end)).join(' and ');
    parts.push(`${m.longLabel}: no bill ends this month; covered by ${by}.`);
  } else if (m.status === 'missing') {
    parts.push(`${m.longLabel}: no bill.`);
  } else if (m.status === 'in_progress') {
    parts.push(`${m.longLabel}: in progress, no bill yet.`);
  } else if (m.status === 'upcoming') {
    parts.push(`${m.longLabel}: upcoming.`);
  } else {
    parts.push(`${m.longLabel}: before your first bill.`);
  }

  if (m.lastYearAmount != null) {
    let lastYear = `Last year ${formatCurrency(m.lastYearAmount)}`;
    if (m.status === 'billed' && m.amount != null) {
      const diff = m.amount - m.lastYearAmount;
      lastYear += `, ${diff >= 0 ? '+' : '−'}${formatCurrency(Math.abs(diff))}`;
    }
    parts.push(`${lastYear}.`);
  }
  if (m.hasOverlap) parts.push('Overlaps another bill.');
  return parts.join(' ');
};

// This period's line: an amount once billed, a dash where a bill was expected,
// and blank for months nobody could have been billed for yet.
const currentLine = (m) => {
  if (m.status === 'billed') return formatTileAmount(m.amount) ?? '—';
  if (m.status === 'covered' || m.status === 'missing') return '—';
  return null;
};

const issueLines = ({ overlaps, gaps, noBillsAfterDay }) => [
  ...overlaps.map(
    ({ a, b, days }) =>
      `${formatDateRange(a.period_start, a.period_end)} and ${formatDateRange(b.period_start, b.period_end)} overlap by ${plural(days, 'day')}`,
  ),
  ...gaps.map(
    (g) => `No bill between ${dayLabel(g.startDay)} and ${dayLabel(g.endDay)} (${plural(g.days, 'day')})`,
  ),
  ...(noBillsAfterDay != null ? [`No bills after ${dayLabel(noBillsAfterDay)}`] : []),
];

const arrowClass =
  'inline-flex items-center justify-center min-h-11 min-w-11 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';

/**
 * Rolling bill coverage: the 12 months ending with the current month plus the
 * next 3, paged back a year at a time. Each tile shows this period's amount
 * over last year's same month. All the date rules live in buildCoverageWindow.
 */
const BillCoverageStrip = ({ bills, today }) => {
  const [pagesBack, setPagesBack] = useState(0);
  // The helper clamps pagesBack (e.g. after the oldest bill is deleted), so the
  // arrows step from the clamped value rather than from possibly-stale state.
  const view = buildCoverageWindow(bills, { today, pagesBack });
  const lines = issueLines(view.issues);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-x-3 mb-1">
        <div className="flex items-center min-w-0">
          <button
            type="button"
            className={arrowClass}
            aria-label="Previous 12 months"
            title="Previous 12 months"
            disabled={!view.canGoBack}
            onClick={() => setPagesBack(view.pagesBack + 1)}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs font-semibold uppercase text-[var(--text-secondary)] whitespace-nowrap">
            {view.label}
          </span>
          <button
            type="button"
            className={arrowClass}
            aria-label="Next 12 months"
            title="Next 12 months"
            disabled={!view.canGoForward}
            onClick={() => setPagesBack(view.pagesBack - 1)}
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <span className="text-xs text-[var(--text-muted)]">
          {view.coveredCount} of 12 months covered
        </span>
      </div>

      {/* Five columns on phones, with the lookahead months starting their own
          row; one row on desktop with a narrow spacer column before them.
          Tailwind needs literal class names, so the numbers are spelled out:
          12 = WINDOW_MONTHS, 3 = LOOKAHEAD_MONTHS, col-start-14 = the column
          after the 12 months and the spacer (billCoverage.js). */}
      <ol
        aria-label="Monthly bill coverage"
        className="grid grid-cols-5 md:grid-cols-[repeat(12,minmax(0,1fr))_0.25rem_repeat(3,minmax(0,1fr))] gap-1"
      >
        {view.months.map((m, i) => {
          const description = describeTile(m);
          const current = currentLine(m);
          const firstLookahead = m.isLookahead && !view.months[i - 1]?.isLookahead;
          return (
            <li
              key={m.monthIndex}
              data-status={m.status}
              aria-label={description}
              title={description}
              className={`relative min-w-0 rounded-md py-1 text-center ${STATUS_CLASS[m.status]} ${firstLookahead ? 'col-start-1 md:col-start-14' : ''}`}
            >
              {m.hasOverlap && (
                <span
                  aria-hidden="true"
                  className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-[var(--error)] ring-1 ring-[var(--bg-card)]"
                />
              )}
              <div className="text-[10px] font-semibold leading-tight truncate">
                {m.label}
                {m.yearMarker && (
                  <span className="text-[9px] font-normal opacity-75">{m.yearMarker}</span>
                )}
              </div>
              <div className="text-[10px] leading-tight truncate">{current ?? ' '}</div>
              <div className="text-[10px] leading-tight truncate opacity-70">
                {formatTileAmount(m.lastYearAmount) ?? '—'}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-[var(--text-muted)]">
        <span>Top: this period · Bottom: last year</span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--primary)]" /> Billed
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--secondary-container)]" /> Covered
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--error-container)]" /> Missing
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm border border-dashed border-[var(--border)] bg-[var(--bg-hover)]" />
          Upcoming
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--error)]" /> Overlap
        </span>
      </div>

      {lines.length > 0 && (
        <div className="mt-3 p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]">
          <p className="text-xs font-semibold text-[var(--text-main)] mb-1">Needs a look</p>
          <ul className="text-xs text-[var(--text-secondary)] space-y-1">
            {lines.slice(0, MAX_ISSUES_SHOWN).map((line) => (
              <li key={line}>{line}</li>
            ))}
            {lines.length > MAX_ISSUES_SHOWN && (
              <li>{`and ${lines.length - MAX_ISSUES_SHOWN} more`}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BillCoverageStrip;
